import React, { useReducer, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import { defaultIconState, defaultAppState, appSettings, EXTERNAL_ACCOUNT, EXTERNAL_ORDERS, EXTERNAL_INSTAGRAM } from './apps';
import lurkCat from 'assets/lurk-cat.gif';
import catCar from 'assets/car-catmul.gif';
import dingSound from 'assets/windows_xp_sounds/Windows XP Ding.wav';
import Modal from './Modal';
import Footer from './Footer';
import Windows from './Windows';
import Icons from './Icons';
import PhotoWidget from './PhotoWidget';
import { DashedBox } from 'components';

const initState = {
  apps: defaultAppState,
  nextAppID: defaultAppState.length,
  nextZIndex: defaultAppState.length,
  focusing: FOCUSING.WINDOW,
  icons: defaultIconState,
  selecting: false,
  powerState: POWER_STATE.START,
};
const reducer = (state, action = { type: '' }) => {
  switch (action.type) {
    case ADD_APP:
      const app = state.apps.find(
        _app => _app.component === action.payload.component,
      );
      if (action.payload.multiInstance || !app) {
        return {
          ...state,
          apps: [
            ...state.apps,
            {
              ...action.payload,
              id: state.nextAppID,
              zIndex: state.nextZIndex,
            },
          ],
          nextAppID: state.nextAppID + 1,
          nextZIndex: state.nextZIndex + 1,
          focusing: FOCUSING.WINDOW,
        };
      }
      const apps = state.apps.map(app =>
        app.component === action.payload.component
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    case DEL_APP:
      if (state.focusing !== FOCUSING.WINDOW) return state;
      return {
        ...state,
        apps: state.apps.filter(app => app.id !== action.payload),
        focusing:
          state.apps.length > 1
            ? FOCUSING.WINDOW
            : state.icons.find(icon => icon.isFocus)
            ? FOCUSING.ICON
            : FOCUSING.DESKTOP,
      };
    case FOCUS_APP: {
      const apps = state.apps.map(app =>
        app.id === action.payload
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, minimized: true } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case TOGGLE_MAXIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, maximized: !app.maximized } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case FOCUS_ICON: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: icon.id === action.payload,
      }));
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons,
      };
    }
    case SELECT_ICONS: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: action.payload.includes(icon.id),
      }));
      return {
        ...state,
        icons,
        focusing: FOCUSING.ICON,
      };
    }
    case FOCUS_DESKTOP:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case START_SELECT:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
        selecting: action.payload,
      };
    case END_SELECT:
      return {
        ...state,
        selecting: null,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
};
function WinXP({ sharedProduct, sharedCategory, sharedBrand }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const [sharedOpened, setSharedOpened] = React.useState(false);
  const [lurkVisible, setLurkVisible] = React.useState(false);
  const lurkTimerRef = React.useRef(null);
  const [catCarVisible, setCatCarVisible] = React.useState(false);
  const catCarTimerRef = React.useRef(null);
  const catCarFirstRef = React.useRef(true);
  const musicPlayingRef = React.useRef(false);

  function scheduleLurk() {
    const delay = (5 + Math.random() * 5) * 60 * 1000;
    lurkTimerRef.current = setTimeout(() => setLurkVisible(true), delay);
  }
  function scheduleCatCar() {
    const delay = (3 + Math.random() * 2) * 60 * 1000;
    catCarTimerRef.current = setTimeout(() => {
      if (musicPlayingRef.current) setCatCarVisible(true);
    }, delay);
  }
  function onWinampPlay() {
    musicPlayingRef.current = true;
    if (catCarFirstRef.current) {
      catCarFirstRef.current = false;
      setCatCarVisible(true);
    }
  }
  React.useEffect(() => {
    scheduleLurk();
    return () => clearTimeout(lurkTimerRef.current);
  }, []);
  React.useEffect(() => {
    let buf = '';
    function onKey(e) {
      buf += e.key.toLowerCase();
      if (buf.length > 10) buf = buf.slice(-10);
      if (buf.includes('nig')) {
        buf = '';
        setLurkVisible(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => {
    if (!lurkVisible) return;
    const hideTimer = setTimeout(() => {
      setLurkVisible(false);
      scheduleLurk();
    }, 6000);
    return () => clearTimeout(hideTimer);
  }, [lurkVisible]);
  React.useEffect(() => {
    if (!catCarVisible) return;
    const hideTimer = setTimeout(() => {
      setCatCarVisible(false);
      scheduleCatCar();
    }, 8000);
    return () => clearTimeout(hideTimer);
  }, [catCarVisible]);
  const ref = useRef(null);
  const mouse = useMouse(ref);
  const focusedAppId = getFocusedAppId();

  const onFocusApp = useCallback(id => {
    dispatch({ type: FOCUS_APP, payload: id });
  }, []);
  React.useEffect(() => {
    function onBincoFocus() {
      const bincoApp = state.apps.find(a => a.component === appSettings.Binco.component);
      if (bincoApp) {
        dispatch({ type: FOCUS_APP, payload: bincoApp.id });
        if (bincoApp.minimized) dispatch({ type: MINIMIZE_APP, payload: bincoApp.id });
      }
    }
    window.addEventListener('binco-focus', onBincoFocus);
    return () => window.removeEventListener('binco-focus', onBincoFocus);
  }, [state.apps]);
  const onMaximizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onMinimizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: MINIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onCloseApp = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: DEL_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  function onMouseDownFooterApp(id) {
    if (focusedAppId === id) {
      dispatch({ type: MINIMIZE_APP, payload: id });
    } else {
      dispatch({ type: FOCUS_APP, payload: id });
    }
  }
  function onMouseDownIcon(id) {
    dispatch({ type: FOCUS_ICON, payload: id });
  }
  const aboutText = `binco - Tech Stack & Credits
========================================

binco is a Windows XP-themed e-commerce store
selling oversized tees. The entire shopping experience
lives inside a simulated Windows XP desktop — complete 
with draggable windows, a taskbar, start menu, and the 
classic Bliss wallpaper.

We are not affiliated with the original brands in anyway. 

That said, the collections are hand-picked/ created and 
thoughtfully curated by us - no bluk slop posting. 
Take a look around, you might find something you like :)

It'd mean a lot for us if you could tell your friends about us!
Thanks for stopping by, and enjoy exploring binco.

========================================

STACK
-----
Frontend
- **React 16** - UI framework
- **styled-components** - CSS-in-JS styling

- **WinXP by ShizukuIchi** - The Windows XP desktop simulator 
that serves as our base. Forked from https://github.com/ShizukuIchi/winXP 
- thank you for making this incredible project open source.

- **XP.css by botoxparty** - CSS library for authentic
Windows XP UI components (scrollbars). https://github.com/botoxparty/XP.css

- **Webamp by Jordan Eldredge** - Winamp 2 reimplemented in
HTML5 and JavaScript. https://github.com/captbaritone/webamp

- **Minesweeper** - Included in the original WinXP project
by ShizukuIchi.

Commerce
- **Shopify Storefront API** - Product catalog, cart, and checkout via GraphQL
- **Razorpay** - Payment processing (via Shopify)

Hosting & Deployment
- **Vercel** - Hosting and CI/CD (auto-deploys from GitHub)
- **GitHub** - Source control
- **GoDaddy** - Domain (binco67.in)

CREDITS
-------
WinXP by ShizukuIchi
  github.com/ShizukuIchi/winXP

XP.css by botoxparty
  github.com/botoxparty/XP.css

Webamp by Jordan Eldredge
  github.com/captbaritone/webamp

Windows XP sounds - Microsoft Corporation

"binco" inspired by GTA: San Andreas
  by Rockstar Games

Built with Claude Code 

binco67.in
support@binco67.in`;

  function onDoubleClickIcon(component) {
    if (component === EXTERNAL_ACCOUNT) {
      window.open('https://shopify.com/99290480957/account/profile', '_blank');
      return;
    }
    if (component === EXTERNAL_ORDERS) {
      window.open('https://shopify.com/99290480957/account/orders', '_blank');
      return;
    }
    if (component === EXTERNAL_INSTAGRAM) {
      window.open('https://www.instagram.com/binco67.in/', '_blank');
      return;
    }
    const appSetting = Object.values(appSettings).find(
      setting => setting.component === component,
    );
    // If opening Notepad from "about binco" icon, prefill with about text
    if (appSetting.component === appSettings.Notepad.component) {
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSetting,
          header: { ...appSetting.header, title: 'about binco - Notepad' },
          injectProps: { initialText: aboutText },
        },
      });
      return;
    }
    dispatch({ type: ADD_APP, payload: appSetting });
  }
  function getFocusedAppId() {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(app => !app.minimized);
    return focusedApp ? focusedApp.id : -1;
  }
  function onMouseDownFooter() {
    dispatch({ type: FOCUS_DESKTOP });
  }
  function onShowDialog(type, message, extraProps) {
    if (type === 'error') {
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Error,
          injectProps: { message },
        },
      });
    } else {
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Info,
          injectProps: { message, ...extraProps },
        },
      });
    }
  }
  function onOpenCart() {
    dispatch({
      type: ADD_APP,
      payload: {
        ...appSettings.Cart,
        injectProps: { onShowDialog },
      },
    });
  }
  const productWindowCount = React.useRef(0);
  // Track open product windows: { appId: { productId, color, size } }
  const productWindowSelections = React.useRef({});

  function onRegisterProductWindow(appId, productId, color, size) {
    productWindowSelections.current[appId] = { productId, color, size };
  }

  function onUnregisterProductWindow(appId) {
    delete productWindowSelections.current[appId];
  }

  function onOpenProduct(product, selections) {
    const sel = selections || {};
    const fromCart = !!sel.fromCart;

    // Check if a window with this product is already open
    const entries = Object.entries(productWindowSelections.current);
    for (const [appIdStr, info] of entries) {
      if (info.productId === product.id) {
        if (fromCart) {
          // From cart: only reuse if same color+size
          if (info.color === sel.color && info.size === sel.size) {
            try { new Audio(dingSound).play().catch(() => {}); } catch(e) {}
            dispatch({ type: FOCUS_APP, payload: Number(appIdStr) });
            return;
          }
        } else {
          // From store/suggestions: always reuse existing window
          try { new Audio(dingSound).play().catch(() => {}); } catch(e) {}
          dispatch({ type: FOCUS_APP, payload: Number(appIdStr) });
          return;
        }
      }
    }

    // No match — open new window
    const offset = productWindowCount.current * 30;
    productWindowCount.current = (productWindowCount.current + 1) % 5;
    dispatch({
      type: ADD_APP,
      payload: {
        ...appSettings.ProductViewer,
        defaultOffset: {
          x: appSettings.ProductViewer.defaultOffset.x + offset,
          y: appSettings.ProductViewer.defaultOffset.y + offset,
        },
        header: {
          ...appSettings.ProductViewer.header,
          title: product.name,
        },
        injectProps: { product, onShowDialog, onOpenCart, initialSelections: sel },
      },
    });
  }
  // Auto-open shared product from URL
  React.useEffect(() => {
    if (sharedProduct && !sharedOpened) {
      setSharedOpened(true);
      onOpenProduct(sharedProduct);
    }
  }, [sharedProduct, sharedOpened]);

  function onClickMenuItem(o) {
    if (o === 'Internet' || o === 'Binco' || o === 'binco')
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Binco,
          injectProps: { onOpenProduct },
        },
      });
    else if (o === 'Minesweeper')
      dispatch({ type: ADD_APP, payload: appSettings.Minesweeper });
    else if (o === 'Notepad')
      dispatch({ type: ADD_APP, payload: appSettings.Notepad });
    else if (o === 'Winamp')
      dispatch({ type: ADD_APP, payload: appSettings.Winamp });
    else if (o === 'My Account')
      window.open('https://shopify.com/99290480957/account/profile', '_blank');
    else if (o === 'My Orders')
      window.open('https://shopify.com/99290480957/account/orders', '_blank');
    else if (o === 'Instagram')
      window.open('https://www.instagram.com/binco67.in/', '_blank');
    else if (o === 'Log Off')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    else if (o === 'Turn Off Computer')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.TURN_OFF });
    // All other menu items do nothing
  }
  function onMouseDownDesktop(e) {
    if (e.target === e.currentTarget)
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
  }
  function onMouseUpDesktop(e) {
    dispatch({ type: END_SELECT });
  }
  const onIconsSelected = useCallback(
    iconIds => {
      dispatch({ type: SELECT_ICONS, payload: iconIds });
    },
    [dispatch],
  );
  function onClickModalButton(text) {
    dispatch({ type: CANCEL_POWER_OFF });
    dispatch({
      type: ADD_APP,
      payload: appSettings.Error,
    });
  }
  function onModalClose() {
    dispatch({ type: CANCEL_POWER_OFF });
  }
  return (
    <Container
      ref={ref}
      onMouseUp={onMouseUpDesktop}
      onMouseDown={onMouseDownDesktop}
      state={state.powerState}
    >
      <Icons
        icons={state.icons}
        onMouseDown={onMouseDownIcon}
        onDoubleClick={onDoubleClickIcon}
        displayFocus={state.focusing === FOCUSING.ICON}
        appSettings={appSettings}
        mouse={mouse}
        selecting={state.selecting}
        setSelectedIcons={onIconsSelected}
      />
      <AccountBalloon />
      <DashedBox startPos={state.selecting} mouse={mouse} />
      <Windows
        apps={state.apps.map(app => {
          if (app.component === appSettings.Binco.component)
            return { ...app, injectProps: { ...app.injectProps, onOpenProduct, onOpenCart, sharedCategory, sharedBrand } };
          if (app.component === appSettings.ProductViewer.component)
            return { ...app, injectProps: { ...app.injectProps, onShowDialog, onOpenCart, onOpenProduct, onRegisterProductWindow, onUnregisterProductWindow, appId: app.id } };
          if (app.component === appSettings.Cart.component)
            return { ...app, injectProps: { ...app.injectProps, onShowDialog, onOpenProduct } };
          if (app.component === appSettings.Winamp.component)
            return { ...app, injectProps: { ...app.injectProps, onPlay: onWinampPlay } };
          return app;
        })}
        onMouseDown={onFocusApp}
        onClose={onCloseApp}
        onMinimize={onMinimizeWindow}
        onMaximize={onMaximizeWindow}
        focusedAppId={focusedAppId}
      />
      <LurkCat src={lurkCat} alt="" $visible={lurkVisible} draggable={false} />
      <CatCar src={catCar} alt="" $visible={catCarVisible} draggable={false} />
      <PhotoWidget onOpenProduct={onOpenProduct} />
      <Footer
        apps={state.apps}
        onMouseDownApp={onMouseDownFooterApp}
        focusedAppId={focusedAppId}
        onMouseDown={onMouseDownFooter}
        onClickMenuItem={onClickMenuItem}
        onLurkCat={() => setLurkVisible(true)}
        onCatCar={() => setCatCarVisible(true)}
      />
      {state.powerState !== POWER_STATE.START && (
        <Modal
          onClose={onModalClose}
          onClickButton={onClickModalButton}
          mode={state.powerState}
        />
      )}
    </Container>
  );
}

function AccountBalloon() {
  const [show, setShow] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (sessionStorage.getItem('binco_account_balloon_shown')) return;
    const openTimer = setTimeout(() => { setShow(true); setVisible(true); }, 4000);
    const fadeTimer = setTimeout(() => setVisible(false), 14000);
    const closeTimer = setTimeout(() => { setShow(false); sessionStorage.setItem('binco_account_balloon_shown', '1'); }, 15000);
    return () => { clearTimeout(openTimer); clearTimeout(fadeTimer); clearTimeout(closeTimer); };
  }, []);
  function dismiss() { setVisible(false); setTimeout(() => setShow(false), 1000); sessionStorage.setItem('binco_account_balloon_shown', '1'); }
  if (!show) return null;
  return (
    <AccountBalloonDiv $visible={visible}>
      <div className="ab__container">
        <button className="ab__close" onClick={dismiss} />
        <div className="ab__header">
          <img className="ab__icon" src={require('assets/windowsIcons/msn.png')} alt="" />
          <span className="ab__title">Account & Orders</span>
        </div>
        <p className="ab__text">You can sign in, manage your account, track orders and view your profile using these apps.</p>
      </div>
    </AccountBalloonDiv>
  );
}

const accountFadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;
const accountFadeOut = keyframes`
  0% { opacity: 1; }
  99% { opacity: 0; }
  100% { opacity: 0; display: none; }
`;
const AccountBalloonDiv = styled.div`
  position: absolute;
  top: 45px;
  right: 120px;
  z-index: 10;
  opacity: 0;
  animation: ${({ $visible }) => $visible ? accountFadeIn : accountFadeOut} 1s forwards;
  filter: drop-shadow(2px 2px 1px rgba(0, 0, 0, 0.4));
  .ab__container {
    position: relative;
    border: 1px solid black;
    border-radius: 7px;
    padding: 8px 28px 10px 10px;
    background-color: #ffffe1;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    max-width: 220px;
    &:before {
      content: '';
      position: absolute;
      top: 12px;
      right: -19px;
      width: 0; height: 0;
      border-style: solid;
      border-width: 10px 0 10px 19px;
      border-color: transparent transparent transparent black;
    }
    &:after {
      content: '';
      position: absolute;
      top: 13px;
      right: -17px;
      width: 0; height: 0;
      border-style: solid;
      border-width: 9px 0 9px 17px;
      border-color: transparent transparent transparent #ffffe1;
    }
  }
  .ab__close {
    outline: none;
    position: absolute;
    right: 4px; top: 4px;
    width: 14px; height: 14px;
    border: 1px solid rgba(0,0,0,0.1);
    border-radius: 3px;
    background: transparent;
    cursor: pointer;
    &:before, &:after {
      content: '';
      position: absolute;
      left: 5px; top: 2px;
      height: 8px; width: 2px;
      background: rgba(170,170,170);
    }
    &:before { transform: rotate(45deg); }
    &:after { transform: rotate(-45deg); }
    &:hover { background: #ffa90c; border-color: white; &:before, &:after { background: white; } }
  }
  .ab__header {
    display: flex;
    align-items: center;
    font-weight: 700;
    color: #000;
  }
  .ab__icon { width: 14px; height: 14px; margin-right: 8px; }
  .ab__title { font-size: 11px; }
  .ab__text { margin: 5px 0 0; color: #333; line-height: 1.4; }
`;

const LurkCat = styled.img`
  position: absolute;
  bottom: 0;
  right: 300px;
  height: 80px;
  z-index: 2;
  pointer-events: none;
  transition: transform 1.5s ease-in-out;
  transform: translateY(${({ $visible }) => $visible ? '-30px' : '100%'});
`;

const CatCar = styled.img`
  position: absolute;
  bottom: 0;
  right: 160px;
  height: 120px;
  z-index: 2;
  pointer-events: none;
  transition: transform 1.5s ease-in-out;
  transform: translateY(${({ $visible }) => $visible ? '-30px' : '100%'});
`;

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;
const animation = {
  [POWER_STATE.START]: '',
  [POWER_STATE.TURN_OFF]: powerOffAnimation,
  [POWER_STATE.LOG_OFF]: powerOffAnimation,
};

const Container = styled.div`
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans');
  font-family: Tahoma, 'Noto Sans', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: url(https://i.imgur.com/Zk6TR5k.jpg) no-repeat center center fixed;
  background-size: cover;
  animation: ${({ state }) => animation[state]} 5s forwards;
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

export default WinXP;
